import { useState, useEffect, lazy, Suspense } from "react";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  onAuthStateChanged, sendPasswordResetEmail, updateProfile,
  signInWithPopup
} from "firebase/auth";
import {
  doc, setDoc, getDoc,
  collection, query, where, onSnapshot
} from "firebase/firestore";
import { auth, db, googleProvider } from "./firebase";
import Landing from "./landing/Landing";
import Privacy from "./landing/Privacy";
import Terms from "./landing/Terms";

// El resto de la app (dashboard, ventas, inventario, gráficas con Recharts,
// panel admin, etc.) se carga solo DESPUÉS de iniciar sesión — así la Landing
// pública no descarga ese código pesado.
const AppShell = lazy(() => import("./AppShell"));

// Usado por AuthScreen (login/registro). También existe una copia en
// AppShell.jsx para el resto de la app — es un componente pequeño sin
// dependencias pesadas, así que duplicarlo es más simple que compartirlo.
function Field({ label, value, onChange, type = "text", placeholder = "", disabled, T }) {
  const C = T;
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <label style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      disabled={disabled}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          background: disabled ? `${C.border}80` : C.bgHover, borderRadius: 10, padding: "11px 13px", color: C.text,
          fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%",
          border: `1px solid ${focused ? C.primary : C.border}`, transition: "border 0.15s",
        }} />
    </div>
  );
}

const darkTheme = {
  primary: "#00C896", primaryDark: "#00A878",
  bg: "#08101E", bgCard: "#0D1828", bgHover: "#111E30", bgSidebar: "#060E1A",
  border: "rgba(0,200,150,0.13)", borderHi: "rgba(0,200,150,0.35)",
  text: "#E4EFFF", muted: "#6B80A0", dim: "#3A4560",
  red: "#FF5C5C", amber: "#FFB347", blue: "#4FC3F7", purple: "#CE93D8",
  shadow: "0 4px 24px rgba(0,0,0,0.4)",
};
const lightTheme = {
  primary: "#00A87A", primaryDark: "#008F68",
  bg: "#F0F4F8", bgCard: "#FFFFFF", bgHover: "#F5F9FC", bgSidebar: "#FFFFFF",
  border: "rgba(0,168,122,0.15)", borderHi: "rgba(0,168,122,0.4)",
  text: "#0D1828", muted: "#6B80A0", dim: "#A0B0C0",
  red: "#E53E3E", amber: "#D97706", blue: "#2B6CB0", purple: "#6B46C1",
  shadow: "0 4px 24px rgba(0,0,0,0.08)",
};

function AuthScreen({ C, initialMode = "login", onBack }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const parseError = (code) => ({
    "auth/email-already-in-use": "Este correo ya está registrado.",
    "auth/invalid-email": "Correo inválido.",
    "auth/weak-password": "Contraseña mínimo 6 caracteres.",
    "auth/user-not-found": "No existe cuenta con este correo.",
    "auth/wrong-password": "Contraseña incorrecta.",
    "auth/invalid-credential": "Correo o contraseña incorrectos.",
    "auth/too-many-requests": "Demasiados intentos. Espera un momento.",
  }[code] || "Ocurrió un error. Intenta de nuevo.");

  const handle = async () => {
    setError(""); setSuccess("");
    if (!email) { setError("El correo es requerido."); return; }
    if (mode !== "reset" && !pass) { setError("La contraseña es requerida."); return; }
    if (mode === "register" && !name.trim()) { setError("El nombre es requerido."); return; }
    setLoading(true);
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email.trim(), pass);
      } else if (mode === "register") {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);
        await updateProfile(cred.user, { displayName: name.trim() });
        const trialExpiry = new Date();
        trialExpiry.setDate(trialExpiry.getDate() + 7);
        await setDoc(doc(db, "users", cred.user.uid), {
          name: name.trim(), email: email.trim(), empresa: "", telefono: "",
          currency: "COP", darkMode: true, createdAt: new Date().toISOString(),
          status: "trial", plan: "trial",
          expiresAt: trialExpiry.toISOString().slice(0, 10),
        });
      } else {
        await sendPasswordResetEmail(auth, email.trim());
        setSuccess("¡Correo de recuperación enviado!"); setLoading(false); return;
      }
    } catch (e) { setError(parseError(e.code)); }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(""); setSuccess("");
    setLoading(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      // Si es la primera vez que este usuario entra, le creamos su documento
      // en Firestore igual que al registrarse por correo (prueba de 7 días).
      const userRef = doc(db, "users", cred.user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        const trialExpiry = new Date();
        trialExpiry.setDate(trialExpiry.getDate() + 7);
        await setDoc(userRef, {
          name: cred.user.displayName || "", email: cred.user.email || "",
          empresa: "", telefono: "",
          currency: "COP", darkMode: true, createdAt: new Date().toISOString(),
          status: "trial", plan: "trial",
          expiresAt: trialExpiry.toISOString().slice(0, 10),
        });
      }
    } catch (e) {
      if (e.code === "auth/popup-closed-by-user") {
        // El usuario cerró la ventana de Google; no es un error real, no mostramos nada.
      } else if (e.code === "auth/account-exists-with-different-credential") {
        setError("Ya existe una cuenta con este correo usando otro método de acceso.");
      } else {
        setError(parseError(e.code));
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, flexDirection: "column" }}>

      {/* Background blobs */}
      <div style={{ position: "fixed", top: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: `${C.primary}0C`, filter: "blur(60px)", pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: -60, left: -60, width: 200, height: 200, borderRadius: "50%", background: `${C.blue}0A`, filter: "blur(50px)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 400, position: "relative" }}>

        {onBack && (
          <button onClick={onBack} style={{
            background: "transparent", border: "none", color: C.muted, fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 12,
            display: "flex", alignItems: "center", gap: 6,
          }}>← Volver al inicio</button>
        )}

        {/* Logo + branding */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 82, height: 82, borderRadius: 24, margin: "0 auto 16px", background: `linear-gradient(135deg,${C.primary},${C.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 12px 40px ${C.primary}40`, overflow: "hidden" }}>
            <img src="/logo.webp" alt="CuadraAI" style={{ width: 82, height: 82, objectFit: "contain" }} />
          </div>
          <div style={{ color: C.text, fontSize: 28, fontWeight: 900, letterSpacing: "-0.04em" }}>CuadraAI</div>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 5 }}>Gestión financiera inteligente</div>
        </div>

        {/* Card */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 24, padding: "24px 22px", boxShadow: `0 20px 60px rgba(0,0,0,0.12)` }}>

          {/* Tab switcher */}
          {mode !== "reset" && (
            <div style={{ display: "flex", gap: 4, marginBottom: 22, background: C.bgHover, borderRadius: 14, padding: 4 }}>
              {[["login", "Iniciar sesión"], ["register", "Registrarse"]].map(([m, l]) => (
                <button key={m} onClick={() => { setMode(m); setError(""); }} style={{
                  flex: 1, padding: "10px", borderRadius: 11, border: "none", cursor: "pointer",
                  background: mode === m ? C.primary : "transparent",
                  color: mode === m ? "#fff" : C.muted,
                  fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  transition: "all 0.18s",
                  boxShadow: mode === m ? `0 4px 14px ${C.primary}40` : "none",
                }}>{l}</button>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {mode === "reset" && (
              <div style={{ marginBottom: 4 }}>
                <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Recuperar contraseña</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>Te enviaremos un enlace a tu correo</div>
              </div>
            )}
            {mode === "register" && <Field label="Nombre completo" value={name} onChange={setName} placeholder="Tu nombre" T={C} />}
            <Field label="Correo electrónico" value={email} onChange={setEmail} type="email" placeholder="correo@ejemplo.com" T={C} />
            {mode !== "reset" && <Field label="Contraseña" value={pass} onChange={setPass} type="password" placeholder="••••••••" T={C} />}

            {error && (
              <div style={{ color: "#EF4444", fontSize: 12, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, padding: "10px 13px", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}
            {success && (
              <div style={{ color: "#10B981", fontSize: 12, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 10, padding: "10px 13px", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                {success}
              </div>
            )}

            <button onClick={handle} disabled={loading} style={{
              width: "100%", padding: "13px", borderRadius: 14, border: "none", cursor: loading ? "not-allowed" : "pointer",
              background: loading ? `${C.primary}70` : C.primary, color: "#fff",
              fontWeight: 800, fontSize: 14, fontFamily: "inherit",
              boxShadow: loading ? "none" : `0 6px 20px ${C.primary}45`,
              transition: "all 0.2s", marginTop: 4,
            }}>
              {loading ? "Cargando..." : mode === "login" ? "Iniciar sesión" : mode === "register" ? "Crear cuenta gratis" : "Enviar enlace"}
            </button>

            {mode !== "reset" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 0" }}>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                  <span style={{ color: C.dim, fontSize: 11, fontWeight: 600 }}>o continúa con</span>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                </div>

                <button onClick={handleGoogle} disabled={loading} style={{
                  width: "100%", padding: "12px", borderRadius: 14,
                  border: `1px solid ${C.border}`, cursor: loading ? "not-allowed" : "pointer",
                  background: C.bgCard, color: C.text,
                  fontWeight: 700, fontSize: 13.5, fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  transition: "all 0.2s",
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.42-.22-2.05H12v3.72h6.53c-.13 1.06-.85 2.66-2.44 3.74l-.02.14 3.55 2.71.25.02c2.26-2.06 3.65-5.1 3.65-8.28z"/>
                    <path fill="#34A853" d="M12 23.5c3.24 0 5.96-1.06 7.95-2.88l-3.78-2.87c-1.02.69-2.39 1.17-4.17 1.17-3.18 0-5.88-2.09-6.84-4.96l-.14.01-3.7 2.83-.05.13C3.15 21.02 7.25 23.5 12 23.5z"/>
                    <path fill="#FBBC05" d="M5.16 14.16A6.94 6.94 0 014.8 12c0-.75.13-1.48.35-2.16l-.01-.14-3.75-2.87-.12.06A11.5 11.5 0 00.5 12c0 1.86.45 3.62 1.27 5.17l3.39-3.01z"/>
                    <path fill="#EA4335" d="M12 4.88c2.25 0 3.77.95 4.64 1.75l3.39-3.24C17.95 1.61 15.24.5 12 .5 7.25.5 3.15 2.98 1.16 6.83l3.99 3.01C6.11 6.97 8.82 4.88 12 4.88z"/>
                  </svg>
                  {mode === "register" ? "Registrarse con Google" : "Iniciar sesión con Google"}
                </button>
              </>
            )}

            {mode === "login" && (
              <button onClick={() => { setMode("reset"); setError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textAlign: "center" }}>
                ¿Olvidaste tu contraseña?
              </button>
            )}
            {mode === "reset" && (
              <button onClick={() => { setMode("login"); setError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                ← Volver al inicio de sesión
              </button>
            )}
          </div>
        </div>

        <div style={{ textAlign: "center", color: C.dim, fontSize: 11, marginTop: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.dim} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Datos guardados de forma segura en Firebase
        </div>
      </div>
    </div>
  );
}

// ── APP ROOT ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userDocLoading, setUserDocLoading] = useState(true);
  const [currency, setCurrency] = useState("COP");
  const [mode, setMode] = useState("negocio");
  const [darkMode, setDarkMode] = useState(true);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [legalPage, setLegalPage] = useState(null); // "privacy" | "terms" | null
  const [isAdmin, setIsAdmin] = useState(false);

  const C = darkMode ? darkTheme : lightTheme;

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (u) {
        u.getIdTokenResult(true).then(result => {
          setIsAdmin(result.claims?.admin === true);
        }).catch(() => setIsAdmin(false));
        getDoc(doc(db, "users", u.uid)).then(snap => {
          if (snap.exists()) {
            const data = snap.data();
            setUserDoc(data);
            if (data.currency) setCurrency(data.currency);
            if (data.appMode) setMode(data.appMode);
            if (data.darkMode !== undefined) setDarkMode(data.darkMode);
          }
          setUserDocLoading(false);
        }).catch(e => {
          console.warn("Error loading user doc:", e);
          setUserDocLoading(false);
        });
      } else {
        setIsAdmin(false);
        setUserDocLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "products"), where("uid", "==", user.uid));
    return onSnapshot(q,
      snap => {
        setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      err => console.error("Products Firestore error:", err.code, err.message)
    );
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "transactions"), where("uid", "==", user.uid));
    return onSnapshot(q,
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setTransactions(docs);
      },
      err => console.error("Transactions Firestore error:", err.code, err.message)
    );
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "receivables"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      setReceivables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [user]);

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: darkTheme.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <style>{`@keyframes cuadraspin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 80, height: 80, borderRadius: 22,
          background: `linear-gradient(135deg, ${darkTheme.primary}, ${darkTheme.primaryDark})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 8px 32px ${darkTheme.primary}50`,
          overflow: "hidden",
        }}>
          <img src="/logo.webp" alt="CuadraAI" width="50" height="50" style={{ width: 50, height: 50, objectFit: "contain" }} />
        </div>
        <div style={{ color: darkTheme.text, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>CuadraAI</div>
        <div style={{ color: darkTheme.muted, fontSize: 13 }}>Cargando tu información...</div>
        <div style={{ width: 34, height: 34, borderRadius: "50%", border: `3px solid rgba(0,200,150,0.2)`, borderTopColor: darkTheme.primary, animation: "cuadraspin 0.8s linear infinite", marginTop: 8 }} />
      </div>
    );
  }

  if (!user) {
    if (legalPage === "privacy") return <Privacy C={C} onBack={() => setLegalPage(null)} />;
    if (legalPage === "terms") return <Terms C={C} onBack={() => setLegalPage(null)} />;
    if (showAuth) return <AuthScreen C={C} initialMode={authMode} onBack={() => setShowAuth(false)} />;
    return (
      <Landing
        C={C}
        onLogin={() => { setAuthMode("login"); setShowAuth(true); }}
        onRegister={() => { setAuthMode("register"); setShowAuth(true); }}
        onNavigate={(page) => setLegalPage(page)}
      />
    );
  }

  // Wait for userDoc to load before checking subscription
  if (userDocLoading) {
    return (
      <div style={{ minHeight: "100vh", background: darkTheme.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <style>{`@keyframes cuadraspin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 80, height: 80, borderRadius: 22, background: `linear-gradient(135deg, ${darkTheme.primary}, ${darkTheme.primaryDark})`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          <img src="/logo.webp" alt="CuadraAI" width="50" height="50" style={{ width: 50, height: 50, objectFit: "contain" }} />
        </div>
        <div style={{ color: darkTheme.text, fontSize: 24, fontWeight: 800 }}>CuadraAI</div>
        <div style={{ width: 34, height: 34, borderRadius: "50%", border: `3px solid rgba(0,200,150,0.2)`, borderTopColor: darkTheme.primary, animation: "cuadraspin 0.8s linear infinite" }} />
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div style={{ minHeight: "100vh", background: darkTheme.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", border: `3px solid rgba(0,200,150,0.2)`, borderTopColor: darkTheme.primary, animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    }>
      <AppShell
        user={user}
        userDoc={userDoc}
        setUserDoc={setUserDoc}
        isAdmin={isAdmin}
        currency={currency}
        setCurrency={setCurrency}
        mode={mode}
        setMode={setMode}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        products={products}
        transactions={transactions}
        receivables={receivables}
        C={C}
      />
    </Suspense>
  );
}
